import { type FC, useEffect, useRef } from "react"

import useHorizontalScroll from "@forento/ui/hooks/useHorizontalScroll"
import { cn } from "@forento/ui/utilities/tailwind"

import { ChevronIcon } from "~/components/Icon"

type Props = {
	channels: { id: number | null; name: string }[]
	selectedChannelId: number | null
	setSelectedChannelId: (id: number | null) => void
}
const ChannelSelector: FC<Props> = ({ channels, selectedChannelId, setSelectedChannelId }) => {
	const { ref, canScrollToStart, canScrollToEnd, scroll } = useHorizontalScroll()
	const selectedRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		selectedRef.current?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" })
	}, [])

	return (
		<div className="bg-component-background relative rounded-lg p-1.5">
			<div ref={ref} className="flex w-0 min-w-full scrollbar-none gap-1 overflow-x-auto scroll-smooth">
				{channels.map(({ id, name }) => {
					const isSelected = id === selectedChannelId
					return (
						<button
							key={id ?? "general"}
							ref={isSelected ? selectedRef : undefined}
							type="button"
							onClick={() => setSelectedChannelId(id)}
							className={cn(
								"shrink-0 cursor-pointer rounded-md px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors",
								isSelected
									? "text-primary bg-white shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_rgba(16,24,40,0.06)]"
									: "text-light hover:text-black",
							)}
						>
							{name}
						</button>
					)
				})}
			</div>
			<ScrollButton direction="start" isVisible={canScrollToStart} onClick={() => scroll("start")} />
			<ScrollButton direction="end" isVisible={canScrollToEnd} onClick={() => scroll("end")} />
		</div>
	)
}

type ScrollButtonProps = { direction: "start" | "end"; isVisible: boolean; onClick: () => void }
const ScrollButton: FC<ScrollButtonProps> = ({ direction, isVisible, onClick }) => {
	if (!isVisible) return null

	const isStart = direction === "start"

	return (
		<div
			className={cn(
				"from-component-background pointer-events-none absolute inset-y-1.5 flex w-16 items-center from-50% to-transparent",
				isStart ? "left-1.5 justify-start bg-linear-to-r pr-4" : "right-1.5 justify-end bg-linear-to-l pl-4",
			)}
		>
			<button
				type="button"
				aria-label={isStart ? "Scroll channels left" : "Scroll channels right"}
				onClick={onClick}
				className="text-light pointer-events-auto flex size-7 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0_1px_3px_rgba(16,24,40,0.15)] transition-colors hover:text-black"
			>
				<ChevronIcon direction={isStart ? "left" : "right"} className="size-4" />
			</button>
		</div>
	)
}

export default ChannelSelector
